import React from "react";
import { CalendarDays, ChevronRight, Eye, Loader2, MessageSquareText } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { el } from "date-fns/locale";
import { LeadStageType } from "@/context/LeadContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LeadQuickActions from "@/components/partners/LeadQuickActions";
import { customerCommentText } from "@/utils/leadComments";
import {
  PartnerLead,
  PARTNER_STAGES,
  SERVICE_LABELS,
  TIMEFRAME_LABELS,
  parsePortalDate,
  stageMeta,
} from "@/types/partnerPortal";
import { cn } from "@/lib/utils";
import RenovationProgramBadge from "@/components/ui-custom/RenovationProgramBadge";

interface PartnerLeadCardProps {
  lead: PartnerLead;
  partnerName?: string;
  onOpen: (leadId: string) => void;
  onStageChange: (leadId: string, stage: LeadStageType) => void;
  updating?: boolean;
  compact?: boolean;
}

const PartnerLeadCard: React.FC<PartnerLeadCardProps> = ({
  lead,
  partnerName,
  onOpen,
  onStageChange,
  updating = false,
  compact = false,
}) => {
  const stage = stageMeta(lead.stage);
  const comment = customerCommentText(lead.comments);
  const createdDate = parsePortalDate(lead.createdAt);

  return (
    <article
      className={cn(
        "rounded-2xl border bg-white transition-colors",
        compact ? "border-slate-200 p-3.5" : "border-slate-200 p-4 sm:p-5",
        updating && "pointer-events-none opacity-60",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => onOpen(lead.id)}
          className="min-w-0 flex-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
        >
          <div className="flex items-center gap-2">
            <h3 className="truncate text-[15px] font-bold text-slate-950">{lead.name}</h3>
            {!lead.linkOpened && (
              <span className="h-2 w-2 shrink-0 rounded-full bg-sky-500" aria-label="Δεν έχει προβληθεί" />
            )}
          </div>
          <p className="mt-1 truncate text-sm font-medium text-slate-600">{lead.phone}</p>
        </button>
        <Badge variant="outline" className={cn("shrink-0 px-2 py-1 text-[11px] font-semibold", stage.badgeClass)}>
          {stage.shortLabel}
        </Badge>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <RenovationProgramBadge service={lead.service} compact />
        <Badge variant="secondary" className="bg-slate-100 text-[11px] font-medium text-slate-700">
          {SERVICE_LABELS[lead.service] || lead.service}
        </Badge>
        {lead.timeframe && (
          <Badge
            variant="outline"
            className={cn(
              "text-[11px] font-medium",
              lead.timeframe === "άμεσα"
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-slate-200 text-slate-600",
            )}
          >
            {TIMEFRAME_LABELS[lead.timeframe] || lead.timeframe}
          </Badge>
        )}
        {lead.linkOpened && (
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[11px] text-emerald-700">
            <Eye className="mr-1 h-3 w-3" /> Προβλήθηκε
          </Badge>
        )}
      </div>

      {!compact && comment && (
        <button
          type="button"
          onClick={() => onOpen(lead.id)}
          className="mt-3 flex w-full items-start gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-left text-xs leading-5 text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
        >
          <MessageSquareText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="line-clamp-2">{comment}</span>
        </button>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-500">
        <span className="flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" />
          {formatDistanceToNow(createdDate, { addSuffix: true, locale: el })}
        </span>
        {!lead.linkOpened && <span className="font-medium text-sky-700">Δεν προβλήθηκε</span>}
      </div>

      <div className="mt-4 border-t border-slate-100 pt-4">
        <LeadQuickActions
          phone={lead.phone}
          email={lead.email}
          name={lead.name}
          service={SERVICE_LABELS[lead.service] || lead.service}
          partnerName={partnerName}
          compact
        />
      </div>

      <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-2">
        <Select
          value={lead.stage}
          onValueChange={(value) => onStageChange(lead.id, value as LeadStageType)}
          disabled={updating}
        >
          <SelectTrigger className="h-11 min-w-0 rounded-xl border-slate-200 bg-white text-xs font-semibold">
            {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <SelectValue />}
          </SelectTrigger>
          <SelectContent>
            {PARTNER_STAGES.map((item) => (
              <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpen(lead.id)}
          className="h-11 rounded-xl border-slate-200 px-3 text-slate-700"
        >
          Στοιχεία <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </article>
  );
};

export default PartnerLeadCard;
